/**
 * Schweizer QR-Rechnung (Swiss Payment Standards, „Swiss Implementation Guidelines QR-bill“ Version 2.3): Nutzdaten des
 * Swiss QR Code, Referenzen mit Prüfziffern und die Entscheidung, ob ein Beleg einen Zahlteil bekommt. Rein; gezeichnet
 * wird im PDF-Schreiber (invoice-pdf.ts) und in der Vorschau im Portal.
 *
 * Nutzdaten (Zeilen, getrennt durch LF, ohne Zeilenende nach dem letzten Element):
 *   SPC · 0200 · 1 · Konto (IBAN bzw. QR-IBAN, CH/LI, 21 Zeichen) · Zahlungsempfänger (Adresstyp S: Name, Strasse,
 *   Hausnummer, PLZ, Ort, Land) · 7 leere Felder (endgültiger Zahlungsempfänger, reserviert) · Betrag (leer oder
 *   „1234.50“) · Währung (CHF/EUR) · Zahlungspflichtiger (Adresstyp S oder 7 leere Felder) · Referenztyp (QRR/SCOR/NON)
 *   · Referenz · unstrukturierte Mitteilung · Trailer EPD.
 *
 * Regeln:
 * - QR-IBAN (Institutsidentifikation 30000–31999) verlangt eine QR-Referenz (QRR, 27 Ziffern, Prüfziffer Modulo 10
 *   rekursiv); eine normale IBAN erlaubt nur eine Creditor Reference nach ISO 11649 (SCOR, „RF“ + Prüfziffern Modulo 97)
 *   oder keine Referenz (NON).
 * - Seit November 2025 nur noch strukturierte Adressen (Typ S); die Hausnummer wird, wenn eindeutig, von der Strasse
 *   getrennt.
 * - Zeichensatz: Latin (Basic Latin, Latin-1 Supplement, Latin Extended-A, Ș ș Ț ț, €); anderes wird ersetzt bzw.
 *   entfernt. Längen je Feld wie im Standard; höchstens 997 Zeichen, damit der Code in Version 25 (Stufe M) passt.
 * - Referenz aus der Rechnungsnummer: QRR aus deren Ziffern (rechtsbündig, mit optionaler Kundenidentifikation der Bank
 *   vorn), SCOR aus deren Buchstaben und Ziffern.
 */
import { encodeQr, utf8Bytes, type QrCode } from "./qr-code.js";
import type { Cents, Language } from "./types.js";

export type SwissQrReferenceType = "QRR" | "SCOR" | "NON";
export type SwissQrCurrency = "CHF" | "EUR";

export interface SwissQrAddress {
  name: string;
  street: string | null;
  buildingNumber: string | null;
  postalCode: string;
  town: string;
  /** ISO-3166-1 alpha-2 */
  country: string;
}

export interface SwissQrBill {
  /** IBAN bzw. QR-IBAN ohne Leerzeichen */
  account: string;
  creditor: SwissQrAddress;
  /** null = Betrag offen (leeres Feld zum Ausfüllen) */
  amountCents: Cents | null;
  currency: SwissQrCurrency;
  debtor: SwissQrAddress | null;
  referenceType: SwissQrReferenceType;
  /** ohne Leerzeichen; bei NON leer */
  reference: string;
  /** unstrukturierte Mitteilung (höchstens 140 Zeichen) */
  message: string | null;
  /** Sprache der Überschriften im Zahlteil */
  language: Language;
}

// ---------------------------------------------------------------------------
// Prüfziffern und Konten
// ---------------------------------------------------------------------------

const MOD10_TABLE = [0, 9, 4, 6, 8, 2, 7, 1, 3, 5];

/** Prüfziffer Modulo 10 rekursiv (QR-Referenz, früher ESR) */
export function mod10Recursive(digits: string): number {
  let carry = 0;
  for (const ch of digits) {
    const d = ch.charCodeAt(0) - 48;
    if (d < 0 || d > 9) throw new Error("Modulo 10 rekursiv: nur Ziffern");
    carry = MOD10_TABLE[(carry + d) % 10]!;
  }
  return (10 - carry) % 10;
}

/** Rest Modulo 97 einer alphanumerischen Zeichenkette (A=10 … Z=35), wie bei IBAN und ISO 11649 */
function mod97(s: string): number {
  let rem = 0;
  for (const ch of s.toUpperCase()) {
    const v = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + (d.charCodeAt(0) - 48)) % 97;
  }
  return rem;
}

export const compactIban = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, "").toUpperCase();

/** IBAN aus der Schweiz oder Liechtenstein (21 Zeichen, Prüfziffern stimmen) */
export function isSwissIban(iban: string | null | undefined): boolean {
  const s = compactIban(iban);
  if (!/^(CH|LI)\d{7}[A-Z0-9]{12}$/.test(s)) return false;
  return mod97(s.slice(4) + s.slice(0, 4)) === 1;
}

/** QR-IBAN: Schweizer IBAN mit Institutsidentifikation (QR-IID) 30000–31999 */
export function isQrIban(iban: string | null | undefined): boolean {
  if (!isSwissIban(iban)) return false;
  const iid = Number(compactIban(iban).slice(4, 9));
  return iid >= 30000 && iid <= 31999;
}

/** QR-Referenz: 27 Ziffern, letzte = Prüfziffer, nicht nur Nullen */
export function isValidQrReference(ref: string | null | undefined): boolean {
  const s = (ref ?? "").replace(/\s+/g, "");
  if (!/^\d{27}$/.test(s) || /^0+$/.test(s)) return false;
  return mod10Recursive(s.slice(0, 26)) === Number(s[26]);
}

/** Creditor Reference nach ISO 11649: „RF“, zwei Prüfziffern, 1–21 Buchstaben/Ziffern */
export function isValidCreditorReference(ref: string | null | undefined): boolean {
  const s = (ref ?? "").replace(/\s+/g, "").toUpperCase();
  if (!/^RF\d{2}[A-Z0-9]{1,21}$/.test(s)) return false;
  return mod97(s.slice(4) + s.slice(0, 4)) === 1;
}

/** Creditor Reference zu einem Referenzteil (1–21 Buchstaben/Ziffern) */
export function creditorReference(base: string): string {
  const b = base.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (!b || b.length > 21) throw new Error("Creditor Reference: 1 bis 21 Buchstaben oder Ziffern");
  const check = 98 - mod97(`${b}RF00`);
  return `RF${String(check).padStart(2, "0")}${b}`;
}

/** Referenzteil für die Creditor Reference aus einer Rechnungsnummer: Buchstaben und Ziffern, höchstens die letzten 21 */
export function scorBaseFromNumber(number: string): string | null {
  const b = number.normalize("NFD").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  return b ? b.slice(-21) : null;
}

/** Kundenidentifikation der Bank (BESR-ID o. Ä.) vor der QR-Referenz: 1–12 Ziffern */
export const isValidQrReferencePrefix = (p: string | null | undefined) => !p || /^\d{1,12}$/.test(p.trim());

/**
 * QR-Referenz aus einer Rechnungsnummer: Kundenidentifikation (falls die Bank eine vergibt) + Ziffern der Nummer
 * rechtsbündig mit Nullen auf 26 Stellen + Prüfziffer. Ohne Ziffern in der Nummer werden Buchstaben als Zahl
 * (A=10 … Z=35) genommen; passt die Nummer nicht, bleiben ihre letzten Stellen.
 * Nicht eindeutig: „RE-2026-1“ und „RE-20261“ ergeben dieselbe Referenz. Deshalb friert die Datenbank die Referenz beim
 * Festschreiben ein und hält sie je Agentur eindeutig (0069, app.freeze_qr_reference); der Beleg nimmt dann diese
 * (SwissQrBillInput.reference). Dieselbe Ableitung steht in SQL (app.qr_reference_of, app.creditor_reference_of).
 */
export function qrReferenceFromNumber(number: string, prefix?: string | null): string | null {
  const p = (prefix ?? "").trim();
  if (!isValidQrReferencePrefix(p)) return null;
  let digits = number.replace(/\D/g, "");
  if (!digits) digits = number.toUpperCase().replace(/[^A-Z]/g, "").split("").map((c) => String(c.charCodeAt(0) - 55)).join("");
  if (!digits || /^0+$/.test(digits)) return null;
  const room = 26 - p.length;
  const body = `${p}${digits.slice(-room).padStart(room, "0")}`;
  return `${body}${mod10Recursive(body)}`;
}

// ---------------------------------------------------------------------------
// Zeichensatz, Adressen, Beträge
// ---------------------------------------------------------------------------

function allowedChar(cp: number): boolean {
  return (cp >= 0x20 && cp <= 0x7e) || (cp >= 0xa0 && cp <= 0x17f) || (cp >= 0x218 && cp <= 0x21b) || cp === 0x20ac;
}

/** Text für ein Feld der Nutzdaten: zulässige Zeichen, eine Zeile, gekürzt */
export function qrText(value: string | null | undefined, max: number): string {
  let out = "";
  for (const ch of (value ?? "").normalize("NFC").replace(/[\r\n\t]+/g, " ")) {
    const cp = ch.codePointAt(0)!;
    if (allowedChar(cp)) { out += ch; continue; }
    const base = ch.normalize("NFD").replace(/\p{M}/gu, "");
    if (base && [...base].every((c) => allowedChar(c.codePointAt(0)!))) out += base;
  }
  return out.replace(/ {2,}/g, " ").trim().slice(0, max).trim();
}

/** „Probeweg 8“ → Strasse und Hausnummer; nur bei eindeutiger Hausnummer am Ende, sonst alles in die Strasse */
export function splitStreet(street: string | null | undefined): { street: string | null; buildingNumber: string | null } {
  const s = qrText(street, 70);
  if (!s) return { street: null, buildingNumber: null };
  const m = /^(.*\D)\s+(\d+\s?[A-Za-z]?(?:\s?[-/]\s?\d+[A-Za-z]?)*)$/.exec(s);
  if (m && m[1]!.trim() && m[2]!.length <= 16) return { street: m[1]!.trim(), buildingNumber: m[2]!.replace(/\s+/g, "") };
  return { street: s, buildingNumber: null };
}

export interface PartyLike {
  name?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
}

/** Strukturierte Adresse (Typ S) aus einer Partei; null, wenn Name, PLZ, Ort oder Land fehlen */
export function swissQrAddress(p: PartyLike | null | undefined): SwissQrAddress | null {
  if (!p) return null;
  const name = qrText(p.name, 70);
  const postalCode = qrText(p.postal_code, 16);
  const town = qrText(p.city, 35);
  const country = (p.country ?? "").trim().toUpperCase();
  if (!name || !postalCode || !town || !/^[A-Z]{2}$/.test(country)) return null;
  const st = splitStreet(p.street);
  return { name, street: st.street, buildingNumber: st.buildingNumber, postalCode, town, country };
}

/** Betrag der Nutzdaten: Punkt, zwei Nachkommastellen, ohne Tausendertrennzeichen */
export function qrAmount(cents: Cents): string {
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

export const QR_AMOUNT_MIN_CENTS = 1;
export const QR_AMOUNT_MAX_CENTS = 99_999_999_999;

// ---------------------------------------------------------------------------
// Nutzdaten
// ---------------------------------------------------------------------------

/** Inhalt des Swiss QR Code (LF als Trenner, Trailer EPD am Ende) */
export function swissQrPayload(b: SwissQrBill): string {
  const addr = (a: SwissQrAddress | null) => (a ? ["S", a.name, a.street ?? "", a.buildingNumber ?? "", a.postalCode, a.town, a.country] : ["", "", "", "", "", "", ""]);
  return [
    "SPC", "0200", "1",
    compactIban(b.account),
    ...addr(b.creditor),
    "", "", "", "", "", "", "",
    b.amountCents == null ? "" : qrAmount(b.amountCents),
    b.currency,
    ...addr(b.debtor),
    b.referenceType,
    b.referenceType === "NON" ? "" : b.reference,
    qrText(b.message, 140),
    "EPD",
  ].join("\n");
}

export type SwissQrProblem =
  | "ACCOUNT" | "QR_IBAN_NEEDS_QRR" | "QRR_NEEDS_QR_IBAN" | "REFERENCE" | "CREDITOR" | "CURRENCY" | "AMOUNT" | "TOO_LONG";

/** Prüft einen Zahlteil gegen den Standard; leere Liste = in Ordnung */
export function checkSwissQrBill(b: SwissQrBill): SwissQrProblem[] {
  const out: SwissQrProblem[] = [];
  if (!isSwissIban(b.account)) out.push("ACCOUNT");
  const qrIban = isQrIban(b.account);
  if (qrIban && b.referenceType !== "QRR") out.push("QR_IBAN_NEEDS_QRR");
  if (!qrIban && b.referenceType === "QRR") out.push("QRR_NEEDS_QR_IBAN");
  if (b.referenceType === "QRR" && !isValidQrReference(b.reference)) out.push("REFERENCE");
  if (b.referenceType === "SCOR" && !isValidCreditorReference(b.reference)) out.push("REFERENCE");
  if (b.referenceType === "NON" && b.reference) out.push("REFERENCE");
  if (!b.creditor.name || !b.creditor.postalCode || !b.creditor.town || !/^[A-Z]{2}$/.test(b.creditor.country)) out.push("CREDITOR");
  if (b.currency !== "CHF" && b.currency !== "EUR") out.push("CURRENCY");
  if (b.amountCents != null && (!Number.isInteger(b.amountCents) || b.amountCents < QR_AMOUNT_MIN_CENTS || b.amountCents > QR_AMOUNT_MAX_CENTS)) out.push("AMOUNT");
  if (utf8Bytes(swissQrPayload(b)).length > 997) out.push("TOO_LONG");
  return out;
}

/** Der Swiss QR Code eines Zahlteils: Byte-Modus, Fehlerkorrektur M, höchstens Version 25 */
export function swissQrCode(b: SwissQrBill): QrCode {
  return encodeQr(swissQrPayload(b), { ecc: "M", maxVersion: 25 });
}

// ---------------------------------------------------------------------------
// Entscheidung: bekommt ein Beleg einen Zahlteil?
// ---------------------------------------------------------------------------

/** Einstellung der Agentur (org_settings, eingefroren im Bankteil des Belegs) */
export interface SwissQrSettings {
  /** false = nie einen Zahlteil drucken */
  enabled: boolean;
  /** Referenz bei normaler IBAN: Creditor Reference (Standard) oder keine */
  reference: "scor" | "non";
  /** Kundenidentifikation der Bank vor der QR-Referenz (nur mit QR-IBAN) */
  prefix: string | null;
}
export const DEFAULT_SWISS_QR_SETTINGS: SwissQrSettings = { enabled: true, reference: "scor", prefix: null };

export interface SwissQrBillInput {
  /** Rechnungsnummer (ohne Nummer, etwa im Entwurf, kein Zahlteil) */
  number: string | null;
  language: Language;
  currency: string;
  grossCents: Cents;
  /** Zahlungsempfänger = Aussteller des Belegs */
  creditor: PartyLike;
  iban: string | null;
  qrIban?: string | null;
  debtor?: PartyLike | null;
  settings?: SwissQrSettings | null;
  /**
   * Beim Festschreiben eingefrorene Referenz (issuer_snapshot.bank.qr.ref, 0069): geht der Ableitung aus der Nummer vor,
   * wenn sie zur Art passt (QR-Referenz bei QR-IBAN, Creditor Reference sonst).
   */
  reference?: string | null;
}

export type SwissQrSkip = "DISABLED" | "NO_SWISS_ACCOUNT" | "CURRENCY" | "AMOUNT" | "CREDITOR_ADDRESS" | "NO_NUMBER" | "REFERENCE";

/**
 * Zahlteil für eine Rechnung, deren Aussteller das Geld bekommt (Rechnung der Agentur an Marke oder Creator, Rechnung des
 * Creators). Nur mit Konto in der Schweiz oder Liechtenstein und in CHF oder EUR; sonst der Grund, warum nicht.
 */
export function swissQrBillFor(input: SwissQrBillInput): { bill: SwissQrBill; skip: null } | { bill: null; skip: SwissQrSkip } {
  const settings = input.settings ?? DEFAULT_SWISS_QR_SETTINGS;
  if (!settings.enabled) return { bill: null, skip: "DISABLED" };
  const account = isQrIban(input.qrIban) ? compactIban(input.qrIban) : isSwissIban(input.iban) ? compactIban(input.iban) : null;
  if (!account) return { bill: null, skip: "NO_SWISS_ACCOUNT" };
  if (input.currency !== "CHF" && input.currency !== "EUR") return { bill: null, skip: "CURRENCY" };
  if (!Number.isInteger(input.grossCents) || input.grossCents < QR_AMOUNT_MIN_CENTS || input.grossCents > QR_AMOUNT_MAX_CENTS) return { bill: null, skip: "AMOUNT" };
  const creditor = swissQrAddress(input.creditor);
  if (!creditor) return { bill: null, skip: "CREDITOR_ADDRESS" };
  const number = input.number?.trim() || null;
  if (!number) return { bill: null, skip: "NO_NUMBER" };
  let referenceType: SwissQrReferenceType;
  let reference = "";
  const frozen = (input.reference ?? "").replace(/\s+/g, "").toUpperCase() || null;
  if (isQrIban(account)) {
    const r = frozen && isValidQrReference(frozen) ? frozen : qrReferenceFromNumber(number, settings.prefix);
    if (!r) return { bill: null, skip: "REFERENCE" };
    referenceType = "QRR";
    reference = r;
  } else if (settings.reference === "scor") {
    const base = frozen && isValidCreditorReference(frozen) ? null : scorBaseFromNumber(number);
    reference = frozen && isValidCreditorReference(frozen) ? frozen : base ? creditorReference(base) : "";
    referenceType = reference ? "SCOR" : "NON";
  } else {
    referenceType = "NON";
  }
  const bill: SwissQrBill = {
    account, creditor, amountCents: input.grossCents, currency: input.currency, debtor: swissQrAddress(input.debtor),
    referenceType, reference, message: qrText(`${input.language === "en" ? "Invoice" : "Rechnung"} ${number}`, 140), language: input.language,
  };
  return checkSwissQrBill(bill).length ? { bill: null, skip: "REFERENCE" } : { bill, skip: null };
}

// ---------------------------------------------------------------------------
// Anzeige im Zahlteil
// ---------------------------------------------------------------------------

/** IBAN in Vierergruppen */
export const qrAccountDisplay = (iban: string) => compactIban(iban).replace(/(.{4})/g, "$1 ").trim();

/** QR-Referenz in Fünfergruppen von rechts („21 00000 00003 13947 14300 09017“), Creditor Reference in Vierergruppen */
export function qrReferenceDisplay(type: SwissQrReferenceType, ref: string): string {
  const s = ref.replace(/\s+/g, "");
  if (type === "QRR") {
    const head = s.length % 5;
    const groups = [s.slice(0, head), ...(s.slice(head).match(/.{5}/g) ?? [])].filter(Boolean);
    return groups.join(" ");
  }
  if (type === "SCOR") return s.replace(/(.{4})/g, "$1 ").trim();
  return s;
}

/** Betrag im Zahlteil: Leerzeichen als Tausendertrennzeichen, Punkt als Dezimaltrennzeichen („1 234.50“) */
export function qrAmountDisplay(cents: Cents): string {
  const [int, dec] = qrAmount(cents).split(".") as [string, string];
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}.${dec}`;
}

/** Adresszeilen im Zahlteil: Name, Strasse mit Hausnummer, PLZ und Ort (Ländercode davor, wenn nicht CH/LI) */
export function qrAddressLines(a: SwissQrAddress): string[] {
  const street = [a.street, a.buildingNumber].filter(Boolean).join(" ");
  const town = `${a.country === "CH" || a.country === "LI" ? "" : `${a.country}-`}${a.postalCode} ${a.town}`;
  return [a.name, street, town].filter((x) => x.trim());
}

export const QR_BILL_LABELS: Record<Language, {
  paymentPart: string; receipt: string; account: string; reference: string; additional: string; payableBy: string; payableByBlank: string;
  currency: string; amount: string; acceptance: string; separate: string;
}> = {
  de: {
    paymentPart: "Zahlteil", receipt: "Empfangsschein", account: "Konto / Zahlbar an", reference: "Referenz", additional: "Zusätzliche Informationen",
    payableBy: "Zahlbar durch", payableByBlank: "Zahlbar durch (Name/Adresse)", currency: "Währung", amount: "Betrag", acceptance: "Annahmestelle",
    separate: "Vor der Einzahlung abzutrennen",
  },
  en: {
    paymentPart: "Payment part", receipt: "Receipt", account: "Account / Payable to", reference: "Reference", additional: "Additional information",
    payableBy: "Payable by", payableByBlank: "Payable by (name/address)", currency: "Currency", amount: "Amount", acceptance: "Acceptance point",
    separate: "Separate before paying in",
  },
};

/** Klartext, warum kein Zahlteil entsteht (Einstellungen, Vorschau) */
export function swissQrSkipText(skip: SwissQrSkip): string {
  switch (skip) {
    case "DISABLED": return "Der QR-Zahlteil ist in den Einstellungen ausgeschaltet.";
    case "NO_SWISS_ACCOUNT": return "Kein Konto in der Schweiz oder in Liechtenstein für diese Währung.";
    case "CURRENCY": return "QR-Rechnungen gibt es nur in CHF und EUR.";
    case "AMOUNT": return "Der Betrag liegt außerhalb dessen, was ein QR-Zahlteil tragen kann.";
    case "CREDITOR_ADDRESS": return "Name, PLZ, Ort oder Land des Zahlungsempfängers fehlen.";
    case "NO_NUMBER": return "Der QR-Zahlteil entsteht mit der Rechnungsnummer beim Festschreiben.";
    case "REFERENCE": return "Aus der Rechnungsnummer ließ sich keine gültige Referenz bilden.";
  }
}
